import React from 'react';
import { ThemeProvider, createTheme, Direction, experimental_extendTheme as extendTheme } from '@mui/material/styles';
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';

// Create RTL cache
const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

// Create LTR cache
const cacheLtr = createCache({
    key: 'mui',
});

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProviderWithDirection: React.FC<ThemeProviderProps> = ({ children }) => {
    const { i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';

    const theme = extendTheme({
        direction: (isRtl ? 'rtl' : 'ltr') as Direction,
        colorSchemes: {
            light: {
                palette: {
                    primary: {
                        main: '#1976d2',
                    },
                    secondary: {
                        main: '#dc004e',
                    },
                    background: {
                        default: '#f5f5f5',
                        paper: '#ffffff',
                    },
                },
            },
            dark: {
                palette: {
                    primary: {
                        main: '#90caf9',
                    },
                    secondary: {
                        main: '#f48fb1',
                    },
                    background: {
                        default: '#121212',
                        paper: '#1e1e1e',
                    },
                },
            },
        },
        typography: {
            fontFamily: isRtl
                ? '"Cairo", "Roboto", sans-serif'
                : '"Poppins", "Inter", sans-serif',
            h1: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Poppins", sans-serif',
                fontWeight: 600,
            },
            h2: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Poppins", sans-serif',
                fontWeight: 600,
            },
            h3: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Poppins", sans-serif',
                fontWeight: 600,
            },
            h4: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Poppins", sans-serif',
                fontWeight: 600,
            },
            h5: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Poppins", sans-serif',
                fontWeight: 600,
            },
            h6: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Poppins", sans-serif',
                fontWeight: 600,
            },
            subtitle1: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Inter", sans-serif',
            },
            subtitle2: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Inter", sans-serif',
            },
            body1: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Inter", sans-serif',
            },
            body2: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Inter", sans-serif',
            },
            button: {
                fontFamily: isRtl ? '"Cairo", sans-serif' : '"Inter", sans-serif',
                fontWeight: 500,
            },
        },
        components: {
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                    },
                },
            },
        },
    });

    return (
        <CacheProvider value={isRtl ? cacheRtl : cacheLtr}>
            <CssVarsProvider theme={theme} defaultMode="light">
                {children}
            </CssVarsProvider>
        </CacheProvider>
    );
}; 